import Image from "next/image";
import { ArrowRight, Info } from "lucide-react";
import { Doctor } from "@/components/DoctorInfoModal";

/**
 * DoctorCard Component
 * Displays a summary of a doctor's profile.
 * Clickable to view full details in a modal.
 * Accessible via keyboard.
 */
export const DoctorCard = ({
  doctor,
  onClick,
}: {
  doctor: Doctor;
  onClick: () => void;
}) => (
  <button
    type="button"
    className="bg-background/60 border-border/50 group focus:ring-primary relative w-full cursor-pointer overflow-hidden rounded-2xl border p-6 text-left shadow-sm backdrop-blur-sm transition-all hover:shadow-md focus:ring-2 focus:ring-offset-2 focus:outline-none"
    onClick={onClick}
    aria-label={`View profile of ${doctor.name}`}
  >
    <div className="bg-primary/5 absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100" />
    <div className="relative z-10">
      <div className="mb-1 flex items-start justify-between">
        <h3 className="group-hover:text-primary text-xl font-bold transition-colors">
          {doctor.name}
        </h3>
        <Info
          className="text-muted-foreground group-hover:text-primary h-5 w-5 transition-colors"
          aria-hidden="true"
        />
      </div>
      {doctor.subtitle && (
        <p className="text-foreground/80 mb-2 text-sm font-medium">
          {doctor.subtitle}
        </p>
      )}
      <p className="text-primary mb-1 text-sm font-semibold">{doctor.role}</p>
      <p className="text-muted-foreground line-clamp-2 text-xs font-medium">
        {doctor.qualification}
      </p>
      <p className="text-primary/80 mt-3 flex items-center text-xs font-medium">
        Read Profile <ArrowRight className="ml-1 h-3 w-3" aria-hidden="true" />
      </p>
    </div>
  </button>
);

/**
 * ImageGrid Component
 * Displays a decorative grid of service images.
 * Images are treated as decorative or atmospheric.
 */
export const ImageGrid = () => {
  const images = [
    { src: "/Services/6.webp", alt: "Skin treatment procedure", offset: false },
    { src: "/Services/8.webp", alt: "Hair treatment session", offset: true },
    { src: "/Services/3.webp", alt: "Consultation in progress", offset: false },
    { src: "/Services/2.webp", alt: "Advanced equipment usage", offset: true },
  ];
  return (
    <div className="relative order-2 w-full xl:sticky xl:top-24 xl:order-2 xl:w-1/2">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:gap-8">
        {images.map((img, i) => (
          <div
            key={i}
            className={`group bg-background relative aspect-square w-full overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 hover:-translate-y-2 ${img.offset ? "xl:mt-12" : ""
              }`}
          >
            <Image
              src={img.src}
              alt={img.alt}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="rounded-2xl object-cover transition-transform duration-700 group-hover:scale-105"
              unoptimized
            />
            <div className="absolute inset-0 rounded-2xl ring-1 ring-black/10 ring-inset" />
          </div>
        ))}
      </div>
      {/* Decorative background element */}
      <div
        className="bg-primary/5 absolute top-1/2 left-1/2 -z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
        aria-hidden="true"
      />
    </div>
  );
};
